// Unified shell context — every page from every sub-app + library lands here.
//
// smbls's auto-router matches `pathname` against `element.routes`, which
// is populated from `context.pages` during preparePages(). All sub-app
// pages get merged into this single map at startup. Sub-apps mounted via
// the dispatch table (canvas, marketplace, preview, logs) keep their own
// DOMQL app definitions, but their page maps merge here.
//
// Domain libraries (tickets, documents, calendar, chat, meet) and auth
// pages flow in through sharedLibraries.
//
// Merge precedence (last writer wins): canvas → logs → workspace (self).
// Marketplace and preview are no longer boot-merged — their routes fold their
// context in lazily (see ./pages/index.js), so shellPages owns every
// /marketplace* and /preview key.
//
// canvas's OWN `'/*': main` catch-all is EXCLUDED from the merge below
// (tickets/workspace.md ASK-USER "should the workspace shell serve project
// CANVAS routes at all?", option 1 — don't serve it here). On this unified
// dev bundle the project DATA loads fine but the SCENE renders empty: the
// canvas editor COMPONENTS are deliberately carved out of this shell's eager
// chunk (#15), and lazily pulling the whole editor graph in on '/*' would
// undo that carve. shellPages registers its OWN `'/*'` (pages/projectRedirect.js)
// that redirects project-shaped paths to the host that runs the full,
// uncarved editor graph instead. Every other canvas page key (dashboard,
// account, signup, studios/:slug, …) is unaffected — they merge exactly as
// before.

import sharedLibraries from './sharedLibraries.js'
import state from './state.js'
import app from './app.js'

import shellPages from './pages/index.js'
import canvasPagesRaw from '../canvas/pages/index.js'
const { '/*': _canvasCatchAll, ...canvasPages } = canvasPagesRaw
// marketplace + preview pages are NOT boot-merged (#16): the shell registers
// every /marketplace* and /preview route lazily in ./pages/index.js
// (lazySharedLib folds the full sub-app context on first mount), so importing
// the eager page barrels here would only re-drag their UI back into the
// initial chunk.

import * as shellComponents from './components/index.js'
// White-label overrides (branded loader + branded login) — spread BELOW,
// and ONLY on a custom-domain host. See components/whitelabel/index.js:
// Nika's constraint is that nothing changes for Symbols, so on a platform
// host these keys are never added to the component graph at all.
import * as whitelabelComponents from './components/whitelabel/index.js'
import { isCustomDomainHost } from './functions/customDomainHost.js'
import * as shellFunctions from './functions/index.js'
import * as shellMethods from './methods/index.js'

// canvas's own slim eager slice (components/functions/methods — still NOT the
// full barrels fanning out to the ~800KB editor, #15) is no longer imported
// directly here: it merges through sharedLibraries.js instead
// (WS-SHELL-CANVAS-EAGERSLICE-MERGE-1), the same declared-library shape
// tickets/calendar/files/documents/chat/meet/analytics already use below.
// Slim eager slice, NOT the full barrels (#16): marketplace's component barrel
// (~274KB — the whole MarketplaceIsland UI tree) + function barrel (~116KB) only
// need to be eager for the shell-root install modal. These re-export just the
// keys the eager shell references before a /marketplace* route mounts; the full
// barrels fold in on first /marketplace* nav (pages/index.js → lazySharedLib).
import * as marketplaceComponents from '../marketplace/components/eagerSlice.js'
import * as marketplaceFunctions from '../marketplace/functions/eagerSlice.js'
// Slim eager slice, NOT the full barrel (#16 preview half): preview is now a
// lazy sub-app — the /preview route (pages/index.js → lazySharedLib) and the
// /preview PageSlot (pages/main.js _previewPage → lazySharedLibPage) fold the
// full preview context (components + functions) in on first mount. The Scene
// iframe boot (renderProject / renderDesignSystem) rides that fold WITH the page
// — its only callers are preview-internal (Scene.js + previewAiTransport.js) —
// so it needn't be eager. The eager slice carries just the preview fns the
// shell's always-mounted Simone Chat references pre-fold (togglePick /
// inspectSelected / setModelMode); the full barrel folds in on first /preview mount.
import * as previewFunctions from '../preview/functions/eagerSlice.js'
// Slim eager slice, NOT the full barrel (#16 preview half): EMPTY — no
// non-preview eager surface bare-keys/extends any preview component before a
// /preview fold, so the whole preview UI tree (Scene/PreviewNavbar/…) folds in
// on first mount rather than riding the eager chunk.
import * as previewComponents from '../preview/components/eagerSlice.js'

// /analytics (project), /workspace-analytics, /workspace-analytics/map,
// /workspace-analytics/logs (+ legacy /analytics/logs + /logs/* aliases)
// — all owned by @symbo-ls/workspace-analytics, consumed as a SHARED
// LIBRARY (registered in sharedLibraries.js). The shared-library
// loader merges its components / functions / state / pages into the
// shell's global scope, so we don't need explicit re-imports here.

import * as globalScope from './globalScope.js'
import config from './config.js'
// The shell's own files plane (the ambient sky masks). Shared libraries
// (brand) still fill every key this map does not hold.
import files from './files/index.js'
import { workspaceDesignSystemSource } from './functions/extensionDesignSystem.js'
import { anchorMainContent } from './functions/anchorMainContent.js'
import { demoRouteGuard } from './functions/demoRouteGuard.js'
import { commRouteGuard } from './functions/commRouteGuard.js'
import { whitelabelRouteGuard } from './functions/whitelabelRouteGuard.js'
import { mwtRouter, mwtRouterHref } from './functions/mwtRouter.js'

import { authRouteGuard } from './functions/authRouteGuard.js'

// DOMQL plugins. `helmet` (tab titles) is the FRAMEWORK's own: createDomql
// registers it from the framework package registry when no plugin of that
// name is present, in both builds. Importing it (and `analyze`) here published
// as `plugins: ["", ""]` — frank stubs every package import — so the
// published shell ran with neither. `analyze` registers the same way when
// `context.analyze` is set; the shell's telemetry is its own analyzing.js
// sink (booted in the root onCreate), and the plugin's taps read
// `context.analyze`, which the shell never set — so it captured nothing in
// either build, and is not declared.
import { DropdownRoot, DropdownTrigger } from '@symbo.ls/dropdown'
import { windowsComponents, windowsFunctions } from '@symbo.ls/windows'
import { tooltipComponents } from '@symbo.ls/tooltip/components'
import { tooltipFunctions } from '@symbo.ls/tooltip/functions'
// - polyglot: NOT declared here, and deliberately so. `context.polyglot` and
//   the polyglot PLUGIN both arrive from @symbo-ls/workspace-shared, which owns
//   the language registry and the i18n bundles. Declaring either one here means
//   importing it across a package boundary, and frank stubs every such import
//   to `""` at publish — measured on this exact file: the shell published
//   `defaultLang: ""`, `languages: ""` and an English base of 0 keys. Worse,
//   `mergeOneLibrary` could not have repaired it: it fills a consumer key only
//   when the consumer has none, and its stub-phantom rescue does not cover
//   `polyglot`. With no `polyglot` key here, its last branch copies the
//   library's whole real object in instead, and `createDomql` (which runs
//   `prepareSharedLibs` BEFORE its plugin auto-registration) then sees a real
//   config and registers the plugin plus every polyglot function itself.
//   That auto-registration also preserves the ONE collision that matters:
//   polyglot ships its own one-argument `setLang`, but the platform's
//   (shared/functions/setLang.js — it loads the i18n bundles, evicts the stale
//   fetch cache and persists the choice) is already a function in the bag by
//   then, and the loop only fills keys that do not hold one.

// Every route def here becomes the shell router's `content` element
// (@symbo.ls/router route-def merge → element.set), so running each def
// through anchorMainContent (functions/anchorMainContent.js — id + role,
// see that file for the full rationale) makes `#main-content` resolve as a
// real `role="main"` landmark on EVERY route, for the SkipToMain link in
// app.js.
const pages = Object.fromEntries(
  Object.entries({
    ...canvasPages,
    ...shellPages
  }).map(([path, def]) => [path, anchorMainContent(def)])
)

export default {
  ...config,

  // This project is ALSO pushed and consumed as a shared library (e.g. by
  // workspace-ui's catalog, `packages/workspace-ui/sharedLibraries.js`
  // `workspace-shell/context.js`). `@symbo.ls/utils` `mergeSharedLibraries`
  // copies any key a consumer doesn't already define — including `app` (this
  // project's ENTIRE root-element tree: AppHeader, ModalRoot, VoiceOverlay,
  // onRenderRouter, …) and `functions.router` (the MWT override below, meant
  // to intercept nav for THIS app alone). A consumer with no `app`/`router`
  // of its own (workspace-ui has neither) silently inherited both — that's
  // tickets/opus.md "shared-library pushes serialize app-wiring functions;
  // shell's `router` hijacked every consumer's navigation": every Link click
  // on the catalog threw `Cannot read properties of undefined (reading
  // 'planMwtNav')`.
  //
  // `sharedLibIgnore` is the sanctioned, already-shipped fix for exactly
  // this class (packages/utils/sharedLibraries.js `mergeOneLibrary`, 18/18
  // tests; live in production on company/packages/brand/context.js —
  // `sharedLibIgnore: ['pages']` keeps brand's own site routes out of every
  // consumer the same way). It is self-describing — declared once here, it
  // protects every CURRENT and FUTURE consumer with zero configuration on
  // their side, unlike a per-consumer `useLibraries` ignore entry.
  //
  // `app` is excluded WHOLE (not the narrower `app/onRenderRouter`):
  // mergeOneLibrary wholesale-copies an entire section when the consumer
  // has no object at that key at all (workspace-ui's case) — a nested path
  // only guards the OTHER branch (both sides already objects). `functions`
  // is intentionally NOT excluded whole — only the `router` override is
  // app-identity-specific; the rest of this project's functions are
  // legitimate, wanted shared-library content. `functions/routerHref` (F2,
  // the Link href twin of `router`) is the same app identity: inherited, it
  // would put THIS shell's `/w/<org>/<workspace>` in front of every consumer
  // app's links.
  sharedLibIgnore: ['app', 'functions/router', 'functions/routerHref'],

  app,
  state,
  sharedLibraries,
  globalScope,
  files,

  pages,

  components: {
    // Register the dropdown portal explicitly for the same reason as the
    // windows system below. The pinned smbls runtime does not inject these
    // components before the shell app is prepared, so a bare `DropdownRoot`
    // mount otherwise becomes a plain full-viewport div. Once the shell's
    // interactive-stage `if` mounts that empty div it inherits normal
    // pointer-events and blocks every AppHeader control after the first
    // interaction. Keep both portal and trigger in the registry so the
    // plugin's pointer-events:none root + interactive Panel contract survives.
    DropdownRoot,
    DropdownTrigger,
    // Register the windows system explicitly. Recent smbls builds no longer
    // guarantee that mergeUiSystem has populated the project registry before
    // the deferred shell ModalRoot is prepared; without this, ModalRoot mounts
    // as an empty div and every booking/dialog opener silently has nowhere to
    // render. Project components below retain normal last-writer precedence.
    ...windowsComponents,
    // The tooltip system, explicitly too — the shell's own pinned
    // @symbo.ls/tooltip (package.json), as the dropdown and windows above. The
    // runtime's merge keeps a project's own definition, so a tooltip release
    // reaches the shell without an smbls bump: the one panel that glides
    // between triggers and the trigger's `tooltipAnchorRect` (3.14.604, Nika
    // 2026-10-25 "make one tooltip sliding") arrived while the shell pinned
    // smbls 3.14.841, whose bundled kit still had neither.
    ...tooltipComponents,
    ...marketplaceComponents,
    ...previewComponents,
    ...shellComponents,
    // Host-gated: `{}` on every platform host, so this spread is a no-op
    // there and the object is byte-identical to before white-label existed.
    ...(isCustomDomainHost() ? whitelabelComponents : {})
  },
  functions: {
    ...windowsFunctions,
    // openTooltip / closeTooltip from the same pinned package as the
    // TooltipRoot above — the panel and its writers must agree on the tip.
    ...tooltipFunctions,
    // closeDropdown (DropdownRoot's Escape/outside-click dismiss) is
    // shared's registration — a real wrapper over the plugin. The bare plugin
    // import registered here published as "" (frank stubs it).
    ...marketplaceFunctions,
    ...previewFunctions,
    ...shellFunctions,
    // Multi-workspace-tabs (Phase 3, §6a) — intercept el.router()/Link nav to
    // emit + consume the `/w/<org-slug>` intranet prefix. Transparent
    // pass-through when the MWT flag is off. Last so it wins over any inherited
    // `router` from the sub-app function maps.
    router: mwtRouter,
    // F2 — the href a Link RENDERS (smbls r17 Link asks `routerHref` for every
    // in-app `/…` href): the same planMwtNav address the click above writes,
    // so a new-tab click or "Copy link address" opens THIS workspace.
    routerHref: mwtRouterHref
  },
  methods: { ...shellMethods },

  // Design system tokens (icons, colors, fonts, spacing, …). Must live
  // under the `designSystem` key — smbls's mergeSharedLibraries calls
  // `deepDefaults(context.designSystem, sharedLib.designSystem)`, so
  // brand fills gaps under this exact path. Target wins on conflicts.
  //
  // The RAW source object (shell tokens + the marketplace integration icons
  // folded into the icon set) is defined once in
  // functions/extensionDesignSystem.js, because the extension-iframe render
  // path needs the very same unprocessed object — `create()` replaces
  // `context.designSystem` with the PROCESSED scratch CONFIG at boot, so by
  // the time an extension renders there is no raw source left to read off the
  // context. See that module for the full input-contract note.
  designSystem: workspaceDesignSystemSource,

  // `workspaceApiUrl` is NOT declared here: it is the DEPLOYMENT's identity,
  // resolved at run time by the boot (shared bootPrepare → workspaceApiBase:
  // host rule → injected channel → fail closed). A module-scope value
  // published as "".

  // Router guards run INSIDE the smbls router before pushState / content
  // mount (see plugins/router runGuards — `true` allows, string redirects).
  // demoRouteGuard blocks isDemo sessions off admin/internal routes pre-mount,
  // so a blocked page never renders, never writes the tab title, and never
  // triggers the re-entrant post-mount bounce (duplicate `content` fallout —
  // tickets/smbls.md).
  // NOTE the key is `router` — smbls createDomql runs
  // `context.routerOptions = initRouter(app, context)` at boot, which DERIVES
  // routerOptions from `context.router` (+ routing defaults) and CLOBBERS any
  // routerOptions set here directly (see workspace/agent.md "write
  // context.router").
  // authRouteGuard: the session gate before mount, on every navigation — the
  // SAME predicate as the parcel cold boot's redirectIfUnauthed
  // (functions/auth.js authRedirectTarget), and the published shell's only
  // gate (mermaid never runs index.js).
  // whitelabelRouteGuard: on a white-label host, a navigation to a URL that
  // names ANOTHER org (/w/<other>, /studio/<other>, /<other>/<project>, the
  // account's personal namespace) lands on this host's home instead
  // (WHITELABEL-STRICT-TENANT-SCOPE-1). A no-op wherever no host pin is set.
  // commRouteGuard: on a comm host (comm.symbols.app — docs/COMM_HOST.md) the
  // bare home lands on /chat and a route the comm profile does not serve opens
  // on the workspace host of the same tier. A no-op on every other host.
  router: { guards: [authRouteGuard, demoRouteGuard, whitelabelRouteGuard, commRouteGuard] },

  // THE PUBLISHED SHELL'S BOOT. Mermaid runs `Smbls.create(app, context)` on
  // the project JSON; index.js and its bootShell never run. This plugin runs
  // the SAME boot sequence bootShell composes (shared bootPrepare /
  // bootResolve / bootApply, through the functions registry):
  //   • beforeCreate (before the first element exists): bootPrepare — the
  //     API base, the polyglot config, the SDK + its 'sdk' fetch adapter and
  //     `context.methods.sdk` (el.sdk on every element);
  //   • then bootResolve, async (auth gate, org + workspace, language) into a
  //     seed; `context.bootReady` holds it. The root's onCreate waits for it,
  //     applies it (bootApply) and only then runs the shell's own auth pass.
  // A parcel boot marks `context.bootMode = 'parcel'` first: nothing runs
  // twice.
  plugins: [
    {
      name: 'workspaceBoot',
      beforeCreate(app, ctx) {
        if (!ctx || ctx.bootMode) return
        const fns = ctx.functions || {}
        if (typeof fns.bootPrepare !== 'function' || typeof fns.bootResolve !== 'function') {
          console.warn('[workspaceBoot] the boot steps are not registered — the shell boots without a session')
          return
        }
        ctx.bootMode = 'published'
        const seed = {}
        ctx.bootSeed = seed
        // Called the way el.call calls a registered function: `this` is an
        // element-shaped receiver, so a published step resolves its helpers
        // through `this.context.globalScope` (the merged library scope).
        const receiver = { context: ctx }
        let prep = null
        try {
          prep = fns.bootPrepare.call(receiver, ctx, { forceSdk: true })
        } catch (err) {
          console.warn('[workspaceBoot] bootPrepare failed:', (err && err.message) || err)
        }
        ctx.bootReady = Promise.resolve(prep && fns.bootResolve.call(receiver, ctx, prep, { state: seed }))
          .catch((err) => {
            console.warn('[workspaceBoot] bootResolve failed:', (err && err.message) || err)
            return null
          })
      }
    }
  ]
}
